import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePostParagraphInteraction, useParagraphInteractionsQuery } from '../hooks/useParagraphInteractions';
import { cn } from '@/utils/cn';

interface Props {
  sectionId: number;
  paragraphIndex: number;
  onCommentClick: () => void;
  theme?: 'white' | 'sepia' | 'dark';
  onReactionSelect?: (reactionKey: string) => void;
  onReactionSuccess?: (reactionKey: string) => void;
  guideTargetId?: string;
}

const REACTION_EMOJIS = [
  { key: 'happy', emoji: '😄' },
  { key: 'love', emoji: '❤️' },
  { key: 'laugh', emoji: '😂' },
  { key: 'surprised', emoji: '😲' },
  { key: 'excited', emoji: '🤩' },
  { key: 'sad', emoji: '😢' },
  { key: 'cry', emoji: '😭' },
  { key: 'fear', emoji: '😨' },
  { key: 'angry', emoji: '😡' },
  { key: 'hot', emoji: '🔥' },
];

const ParagraphInteractionMenu: React.FC<Props> = ({
  sectionId,
  paragraphIndex,
  onCommentClick,
  theme = 'white',
  onReactionSelect,
  onReactionSuccess,
  guideTargetId,
}) => {
  const { mutate: postInteraction } = usePostParagraphInteraction();
  const { data: interactionsData } = useParagraphInteractionsQuery(sectionId, paragraphIndex);
  const isPremium = interactionsData?.is_premium;
  const [selectedReaction, setSelectedReaction] = React.useState<string | null>(null);
  const [interactable, setInteractable] = React.useState(false);
  const [tooltipMsg, setTooltipMsg] = React.useState<string | null>(null);
  const [tooltipX, setTooltipX] = React.useState<number>(0);

  React.useEffect(() => {
    setInteractable(false);
    const timer = setTimeout(() => setInteractable(true), 350);
    return () => clearTimeout(timer);
  }, []);

  React.useEffect(() => {
    setSelectedReaction(interactionsData?.user_reaction ?? null);
  }, [interactionsData?.user_reaction]);

  const showTooltip = (msg: string, x: number) => {
    setTooltipX(x);
    setTooltipMsg(msg);
    setTimeout(() => setTooltipMsg(null), 3000);
  };

  const handleReaction = (e: React.MouseEvent, reactionKey: string) => {
    if (!interactable) return;

    if (isPremium === false) {
      const rect = e.currentTarget.getBoundingClientRect();
      const parent = e.currentTarget.closest('.interaction-menu-popup');
      if (parent) {
        const parentRect = parent.getBoundingClientRect();
        const center = rect.left + rect.width / 2 - parentRect.left;
        showTooltip('برای ثبت واکنش، اشتراک بوکا نیاز است.', center);
      }
      return;
    }

    onReactionSelect?.(reactionKey);
    postInteraction(
      {
        section_id: sectionId,
        paragraph_index: paragraphIndex,
        interaction_type: 'reaction',
        content: reactionKey,
        parent_interaction_id: null,
      },
      {
        onSuccess: () => {
          setSelectedReaction(reactionKey);
          onReactionSuccess?.(reactionKey);
        },
      }
    );
  };

  const handleCommentClick = (e: React.MouseEvent) => {
    if (!interactable) return;

    if (isPremium === false) {
      const rect = e.currentTarget.getBoundingClientRect();
      const parent = e.currentTarget.closest('.interaction-menu-popup');
      if (parent) {
        const parentRect = parent.getBoundingClientRect();
        const center = rect.left + rect.width / 2 - parentRect.left;
        showTooltip('برای دیدن یا ارسال گفت‌وگو، اشتراک بوکا نیاز است.', center);
      }
      return;
    }
    onCommentClick();
  };

  const menuBg = theme === 'dark' ? 'bg-gray-800' : theme === 'sepia' ? 'bg-[#e2d5b8]' : 'bg-white';
  const textColor = theme === 'dark' ? 'text-black' : 'text-white';
  const commentBtnClass =
    theme === 'dark'
      ? 'bg-white hover:bg-gray-100'
      : theme === 'sepia'
        ? 'bg-[#6B4423] hover:bg-[#5b391d]'
        : 'bg-[#6e61ff] hover:bg-[#5a4ed9]';
  const tooltipBg = theme === 'dark' ? 'bg-gray-700 text-gray-100 border-gray-600' : theme === 'sepia' ? 'bg-[#f4ecd8] text-[#5c4a2f] border-[#b89a6a]' : 'bg-white text-gray-800 border-gray-200';

  return (
    <div className="absolute z-[100] -right-2 sm:-right-4" style={{ bottom: '40px' }}>
      <AnimatePresence>
        {tooltipMsg && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.9 }}
            className={cn(
              'absolute -top-2 right-[-4px] mb-3 px-3 py-2 rounded-xl shadow-2xl border text-[10px] font-persian font-bold text-center leading-relaxed w-[150px] whitespace-normal pointer-events-none',
              tooltipBg
            )}
            style={{ transform: 'translateY(-100%)' }}
            dir="rtl"
          >
            {tooltipMsg}
            <div
              className={cn('absolute bottom-[-6px] w-3 h-3 rotate-45 border-r border-b', tooltipBg)}
              style={{ left: `calc(${tooltipX}px + 4px)`, transform: 'translateX(-50%) rotate(45deg)' }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        key="interaction-menu-popup"
        initial={false}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className={cn('interaction-menu-popup w-[150px] sm:w-[190px] rounded-2xl shadow-xl p-1.5 flex flex-col border border-black/5', menuBg)}
        data-guide={guideTargetId}
        style={{ pointerEvents: interactable ? 'auto' : 'none' }}
        onClick={(e) => e.stopPropagation()}
        onPointerDownCapture={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <div className="flex flex-row overflow-x-auto gap-1 pt-2.5 pb-1 hide-scrollbar justify-start items-center px-1" dir="rtl">
          {REACTION_EMOJIS.map((r) => {
            const isSelected = selectedReaction === r.key;
            return (
              <motion.button
                key={r.key}
                disabled={!interactable}
                whileTap={{ scale: 0.85 }}
                onClick={(e) => handleReaction(e, r.key)}
                className={cn(
                  'flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-xl transition-all border',
                  isSelected
                    ? (theme === 'dark' || theme === 'sepia'
                      ? 'bg-white/25 border-white scale-110 shadow-sm'
                      : 'bg-primary-500/20 border-primary-500 scale-110 shadow-sm')
                    : 'bg-black/5 hover:bg-black/10 border-transparent'
                )}
              >
                {r.emoji}
              </motion.button>
            );
          })}
        </div>

        <div className="w-full h-[1px] bg-black/10 mt-1.5 mb-1.5"></div>

        <button
          onClick={(e) => handleCommentClick(e)}
          disabled={!interactable}
          className={cn(
            'w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-persian font-bold transition-colors active:scale-95',
            commentBtnClass,
            textColor
          )}
          dir="rtl"
        >
          <span>گفت و گو کن</span>
        </button>
      </motion.div>
    </div>
  );
};

export default ParagraphInteractionMenu;
